import React, { useState } from 'react';
import { Modal, Form, Button, Alert } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import { API_URL } from '../../config/api';
import styles from './CreateTicketModal.module.scss';

const CreateTicketModal = ({ show, onHide, onTicketCreated }) => {
  const [formData, setFormData] = useState({
    category: '',
    subject: '',
    description: '',
    priority: 'normal'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const categories = [
    { value: 'technical', label: 'Técnico', icon: 'mdi:wrench' },
    { value: 'financial', label: 'Financeiro', icon: 'mdi:currency-usd' },
    { value: 'administrative', label: 'Administrativo', icon: 'mdi:account-supervisor' },
    { value: 'general', label: 'Geral', icon: 'mdi:help-circle' }
  ];

  const priorities = [
    { value: 'low', label: 'Baixa', color: '#10B981' },
    { value: 'normal', label: 'Normal', color: '#3B82F6' },
    { value: 'high', label: 'Alta', color: '#F59E0B' },
    { value: 'urgent', label: 'Urgente', color: '#EF4444' }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validações
    if (!formData.category) {
      setError('Por favor, selecione uma categoria');
      return;
    }
    if (!formData.subject || formData.subject.trim().length < 5) {
      setError('O assunto deve ter pelo menos 5 caracteres');
      return;
    }
    if (!formData.description || formData.description.trim().length < 20) {
      setError('A descrição deve ter pelo menos 20 caracteres');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/tickets`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Erro ao criar ticket');
      }

      // Sucesso
      if (onTicketCreated) {
        onTicketCreated(data.ticket);
      }

      // Resetar formulário
      setFormData({
        category: '',
        subject: '',
        description: '',
        priority: 'normal'
      });

      // Fechar modal
      onHide();
    } catch (err) {
      console.error('Erro ao criar ticket:', err);
      setError(err.message || 'Erro ao criar ticket. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      category: '',
      subject: '',
      description: '',
      priority: 'normal'
    });
    setError('');
    onHide();
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      backdrop="static"
      backdropClassName="modal-backdrop-lightbox"
      className={styles.createTicketModal}
    >
      <Modal.Header closeButton className={styles.modalHeader}>
        <Modal.Title className={styles.modalTitle}>
          <Icon icon="mdi:ticket-plus" className={styles.titleIcon} />
          Abrir Ticket de Suporte
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body className={styles.modalBody}>
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError('')}>
              <Icon icon="mdi:alert-circle" /> {error}
            </Alert>
          )}

          <p className={styles.modalSubtitle}>
            Descreva seu problema ou dúvida e nossa equipe entrará em contato o mais breve possível.
          </p>

          {/* Categoria */}
          <Form.Group className="mb-4">
            <Form.Label className={styles.label}>
              <Icon icon="mdi:format-list-bulleted" /> Categoria *
            </Form.Label>
            <div className={styles.categoryGrid}>
              {categories.map(cat => (
                <div
                  key={cat.value}
                  className={`${styles.categoryCard} ${
                    formData.category === cat.value ? styles.active : ''
                  }`}
                  onClick={() => setFormData(prev => ({ ...prev, category: cat.value }))}
                >
                  <Icon icon={cat.icon} className={styles.categoryIcon} />
                  <span>{cat.label}</span>
                </div>
              ))}
            </div>
          </Form.Group>

          {/* Prioridade */}
          <Form.Group className="mb-4">
            <Form.Label className={styles.label}>
              <Icon icon="mdi:flag" /> Prioridade
            </Form.Label>
            <Form.Select
              name="priority"
              value={formData.priority}
              onChange={handleChange}
              className={styles.select}
            >
              {priorities.map(priority => (
                <option key={priority.value} value={priority.value}>
                  {priority.label}
                </option>
              ))}
            </Form.Select>
            <Form.Text className="text-muted">
              <small>Selecione a urgência do seu problema</small>
            </Form.Text>
          </Form.Group>

          {/* Assunto */}
          <Form.Group className="mb-4">
            <Form.Label className={styles.label}>
              <Icon icon="mdi:text" /> Assunto *
            </Form.Label>
            <Form.Control
              type="text"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              placeholder="Ex: Problema ao realizar saque"
              className={styles.input}
              maxLength={255}
            />
            <Form.Text className="text-muted">
              <small>{formData.subject.length}/255 caracteres</small>
            </Form.Text>
          </Form.Group>

          {/* Descrição */}
          <Form.Group className="mb-4">
            <Form.Label className={styles.label}>
              <Icon icon="mdi:file-document-edit" /> Descrição *
            </Form.Label>
            <Form.Control
              as="textarea"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Descreva detalhadamente seu problema ou dúvida..."
              rows={6}
              className={styles.textarea}
            />
            <Form.Text className="text-muted">
              <small>Mínimo 20 caracteres • {formData.description.length} caracteres digitados</small>
            </Form.Text>
          </Form.Group>

          <Alert variant="info" className="mb-0">
            <Icon icon="mdi:information" /> <strong>Dica:</strong> Quanto mais detalhes você fornecer, mais rápido poderemos ajudá-lo!
          </Alert>
        </Modal.Body>

        <Modal.Footer className={styles.modalFooter}>
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={loading}
            className={styles.cancelButton}
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={loading}
            className={styles.submitButton}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Criando...
              </>
            ) : (
              <>
                <Icon icon="mdi:send" className="me-1" />
                Abrir Ticket
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default CreateTicketModal;

